type ImageSource = HTMLImageElement | ImageBitmap;

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

/**
 * Читает файл как Data URL (base64) — надёжнее blob URL в Safari
 */
function readAsDataUrl(source: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('FileReader failed'));
    reader.readAsDataURL(source);
  });
}

/**
 * Создаёт изображение из Blob/File с fallback для Safari.
 * Safari имеет проблемы с createImageBitmap() и blob URLs — используем Data URL.
 */
async function createImageSafe(source: File | Blob): Promise<ImageSource> {
  if (isSafari) {
    const dataUrl = await readAsDataUrl(source);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image in Safari'));
      img.src = dataUrl;
    });
  }

  return createImageBitmap(source);
}

function closeImageSource(img: ImageSource): void {
  if (img instanceof ImageBitmap) {
    img.close();
  }
}

function getDimensions(img: ImageSource, maxSize = 180) {
  let width = img.width;
  let height = img.height;

  if (width > height) {
    if (width > maxSize) {
      height *= maxSize / width;
      width = maxSize;
    }
  } else {
    if (height > maxSize) {
      width *= maxSize / height;
      height = maxSize;
    }
  }

  return { width, height };
}

/**
 * Получает размеры изображения из файла (Safari-safe)
 */
export async function getImageDimensions(file: File | Blob): Promise<{ width: number; height: number }> {
  const img = await createImageSafe(file);
  const dimensions = { width: img.width, height: img.height };
  closeImageSource(img);
  return dimensions;
}

/**
 * Создает миниатюру JPEG (3-10KB) с приоритетным использованием встроенного превью
 * @param file - Входной файл
 * @returns Promise<string> - Blob URL миниатюры
 */
export async function createThumbnailUrl(file: File): Promise<string> {
  try {
    const previewBlob = await extractJpegPreview(file);
    if (previewBlob) {
      const img = await createImageSafe(previewBlob);

      if (img.width === 0 || img.height === 0) {
        closeImageSource(img);
      } else if (previewBlob.size <= 10 * 1024) {
        closeImageSource(img);
        return URL.createObjectURL(previewBlob);
      } else {
        return compressWithCanvas(img);
      }
    }
  } catch (error) {
    console.warn('Preview extraction failed, using fallback:', error);
  }

  return createCanvasThumbnail(file);
}

function extractJpegPreview(file: File): Promise<Blob | null> {
  return new Promise(resolve => {
    if (!file.type.match('image/jpeg')) {
      resolve(null);
      return;
    }

    const reader = new FileReader();

    reader.onload = e => {
      const array = new Uint8Array(e.target!.result as ArrayBuffer);
      let start: number | null = null;
      let end: number | null = null;

      // Поиск маркеров SOI (Start of Image) и EOI (End of Image)
      for (let i = 2; i < array.length; i++) {
        if (array[i] === 0xff) {
          if (!start && array[i + 1] === 0xd8) {
            // SOI
            start = i;
          } else if (start && array[i + 1] === 0xd9) {
            // EOI
            end = i + 2;
            break;
          }
        }
      }

      resolve(start && end ? new Blob([array.subarray(start, end)], { type: 'image/jpeg' }) : null);
    };

    reader.onerror = () => resolve(null);
    reader.readAsArrayBuffer(file.slice(0, 50000));
  });
}

async function createCanvasThumbnail(file: File, retries = 2): Promise<string> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const img = await createImageSafe(file);
      return compressWithCanvas(img);
    } catch (error) {
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
      } else {
        throw error;
      }
    }
  }

  throw new Error('createCanvasThumbnail: unreachable');
}

async function compressWithCanvas(img: ImageSource): Promise<string> {
  const canvas = document.createElement('canvas');

  const { width, height } = getDimensions(img);

  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  closeImageSource(img);

  let quality = 0.5;
  let blob: Blob;

  // Постепенно уменьшаем качество пока не достигнем 5KB
  do {
    blob = await new Promise<Blob>(resolve => {
      canvas.toBlob(b => resolve(b!), 'image/jpeg', quality);
    });

    quality -= 0.1;
  } while (blob.size > 5 * 1024 && quality >= 0.2);

  // Clean up canvas to prevent memory leak
  canvas.width = 0;
  canvas.height = 0;

  return URL.createObjectURL(blob);
}
